import type { i18n as I18n } from 'i18next';

/**
 * Labels the service sends with its filter catalog (SDD-017 §5.5): a system field or value carries
 * a `labelKey` (`insurance.policies.field.status` — namespace, then key), a product field or value
 * its own `label {de, en}`, because the label is product data.
 */

/** A `labelKey` is `<namespace>.<key>`; the raw resource, so the SERVER's key needs no typed key set. */
export function translateLabelKey(
  i18n: I18n,
  labelKey: string | undefined,
  fallback: string,
): string {
  if (labelKey === undefined) return fallback;
  const dot = labelKey.indexOf('.');
  if (dot <= 0) return fallback;
  const ns = labelKey.slice(0, dot);
  const key = labelKey.slice(dot + 1);
  for (const language of [i18n.language, 'de', 'en']) {
    const resource: unknown = i18n.getResource(language, ns, key);
    if (typeof resource === 'string' && resource !== '') return resource;
  }
  return fallback;
}

export const catalogLanguageOf = (i18n: I18n): 'de' | 'en' =>
  i18n.language.startsWith('en') ? 'en' : 'de';

/**
 * The reader's label of anything the service labels the catalog's way — a field, an enum value,
 * a search `match`: the product's own de/en label, else the system `labelKey` translated, else
 * `fallback`. Screens use it for catalog-driven column headers and match chips.
 */
export function catalogLabel(
  i18n: I18n,
  entry: {
    readonly label?: { readonly de?: string; readonly en?: string };
    readonly labelKey?: string;
  },
  fallback: string,
): string {
  const product = entry.label?.[catalogLanguageOf(i18n)] ?? entry.label?.de;
  if (product !== undefined && product !== '') return product;
  return translateLabelKey(i18n, entry.labelKey, fallback);
}
